import React, { useState, useEffect } from 'react';
import { Screenshot } from '@/types/tool';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ScreenshotCarouselProps {
  screenshots: Screenshot[];
  className?: string;
}

const ScreenshotCarousel: React.FC<ScreenshotCarouselProps> = ({
  screenshots,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance every 5 seconds (must be called before any conditional returns)
  useEffect(() => {
    if (!screenshots || screenshots.length === 0 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [screenshots, isPaused]);

  if (!screenshots || screenshots.length === 0) {
    return (
      <div className={`text-center text-muted-foreground p-8 ${className}`}>
        No screenshots available.
      </div>
    );
  }

  const currentScreenshot = screenshots[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? screenshots.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === screenshots.length - 1 ? 0 : currentIndex + 1);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);
  const handleFocus = () => setIsPaused(true);
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsPaused(false);
    }
  };

  return (
    <div
      className={`space-y-4 ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="relative group">
        {/* Main screenshot display */}
        <div className="relative overflow-hidden rounded-lg border border-border/20 shadow-lg">
          <img
            src={currentScreenshot.imageUrl}
            alt={currentScreenshot.title}
            className="w-full h-auto max-h-[600px] object-contain bg-muted/5"
            loading="eager"
          />
        </div>

        {/* Navigation arrows */}
        {screenshots.length > 1 && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm"
              onClick={goToPrevious}
              aria-label="Previous screenshot"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm"
              onClick={goToNext}
              aria-label="Next screenshot"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Dot indicators */}
      {screenshots.length > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          {screenshots.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${index === currentIndex
                  ? 'bg-primary scale-125'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ScreenshotCarousel;
